import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatAmountWithCode } from '@app/helpers/formatCurrency.helper';
import {
  addHours,
  formatCountdownTo,
  formatDate,
  formatTime,
} from '@app/helpers/formatDateTime.helper';
import {
  getActionType,
  toInvestmentBadgeView,
  type BadgeView,
  type InvestmentRowActionType,
} from '@app/helpers/status.helper';
import type { UserAccount } from '@app/models/account.model';
import type { InvestmentResponse } from '@app/models/admin.model';
import { InvestmentStatus } from '@app/models/investment.models';
import type {
  ComplexPaginatedDataRequest,
  PaginatedData,
} from '@app/models/shared.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { GenericStore } from '@app/stores/generic.store';
import { BadgeCellRendererComponent } from '@app/ui-components/grid-table/badge-cell-renderer/badge-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { CountdownCellRendererComponent } from '@app/ui-components/grid-table/countdown-cell-renderer/countdown-cell-renderer.component';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { InvestmentActionCellRendererComponent } from '@app/ui-components/grid-table/investment-action-cell-renderer/investment-action-cell-renderer.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslateService } from '@ngx-translate/core';
import type {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

type IncomingRow = {
  id: string;
  sentRaw: string;
  sentDate: string;
  sentTime: string;
  identifier: string;
  bidderName: string;
  bidderEmail: string | null;
  bidderType: string | null;
  investorIsExperienced: boolean;
  amount: number | null;
  currency: string | null;
  interestRate?: number | null;
  pendingUntilRaw: string | null;
  pendingUntilDate: string;
  pendingUntilTime: string;
  pendingUntilCountdown: string;
  acceptUntilTarget: string | null;
  investmentProtection: boolean;
  statusId: number | null;
  actionType: InvestmentRowActionType;
};

@Component({
  selector: 'app-incoming-tab',
  standalone: true,
  imports: [CommonModule, GridTableComponent],
  templateUrl: './incoming-tab.component.html',
  styleUrl: './incoming-tab.component.scss',
})
export class IncomingTabComponent implements OnInit {
  @Input({ required: true }) projectId!: string;

  loading = false;
  columns: ColDef<IncomingRow>[] = [];

  private readonly service = inject(ProjectAdminService);
  private readonly userService = inject(UserService);
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly genericStore = inject(GenericStore);
  private readonly destroyRef = inject(DestroyRef);

  datasource: IDatasource = {
    getRows: (params: IGetRowsParams): void => this.loadRows(params),
  };

  ngOnInit(): void {
    this.buildColumns();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'sentDate',
        colId: 'sentDate',
        headerName: this.translate.instant('admin.allProjects.table.received'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (p: ICellRendererParams<IncomingRow>) => ({
          title: p.data?.sentDate ?? '',
          subtitle: p.data?.sentTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        minWidth: 150,
        flex: 0,
        suppressNavigable: true,
        editable: false,
      },
      {
        field: 'identifier',
        headerName: this.translate.instant(
          'admin.allProjects.table.identifier',
        ),
        minWidth: 140,
      },
      {
        field: 'bidderName',
        headerName: this.translate.instant('admin.allProjects.table.bidder'),
        minWidth: 220,
      },
      {
        field: 'bidderType',
        headerName: this.translate.instant(
          'admin.allProjects.table.bidderType',
        ),
        valueFormatter: (p: ValueFormatterParams<IncomingRow>) =>
          p.value
            ? this.translate.instant('admin.allProjects.table.bidderType_legal')
            : this.translate.instant(
                'admin.allProjects.table.bidderType_natural',
              ),
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'investorIsExperienced',
        headerName: this.translate.instant(
          'admin.allProjects.table.bidderRating',
        ),
        valueFormatter: (p: ValueFormatterParams<IncomingRow>) =>
          p.data?.investorIsExperienced === true
            ? this.translate.instant('profile.qualifiedInvestor')
            : this.translate.instant('profile.unQualifiedInvestor'),
        minWidth: 200,
        sortable: false,
      },
      {
        field: 'amount',
        headerName: this.translate.instant('admin.allProjects.table.offer'),
        valueFormatter: (p: ValueFormatterParams<IncomingRow>) =>
          formatAmountWithCode(
            p.data?.amount,
            p.data?.currency,
            this.translate.currentLang,
          ),
        cellClass: 'text-end',
        minWidth: 160,
        flex: 0,
      },
      {
        field: 'interestRate',
        headerName: this.translate.instant(
          'admin.allProjects.table.interestRate',
        ),
        valueFormatter: (p: ValueFormatterParams<IncomingRow>) => {
          if (p.value == null) return '—';
          return `${p.value}%`;
        },
        cellClass: 'text-end',
        minWidth: 120,
      },
      {
        colId: 'acceptUntil',
        headerName: this.translate.instant(
          'admin.allProjects.table.acceptUntil',
        ),
        cellRenderer: CountdownCellRendererComponent,
        cellRendererParams: (p: ICellRendererParams<IncomingRow>) => ({
          data: {
            targetDate: p.data?.acceptUntilTarget ?? null,
            showCountdown:
              p.data?.statusId === InvestmentStatus.UnderReview,
          },
        }),
        minWidth: 120,
      },
      {
        field: 'investmentProtection',
        headerName: this.translate.instant(
          'admin.allProjects.table.investmentProtection',
        ),
        valueFormatter: (p: ValueFormatterParams<IncomingRow>) =>
          p.data?.investmentProtection === true
            ? this.translate.instant('general.yes')
            : this.translate.instant('general.no'),
        minWidth: 140,
      },
      {
        field: 'statusId',
        colId: 'status',
        headerName: this.translate.instant('admin.allProjects.table.status'),
        cellRenderer: BadgeCellRendererComponent,
        cellClass: 'ag-no-ellipsis',
        cellStyle: customRendererCellStyle,
        cellRendererParams: (param: ICellRendererParams<IncomingRow>) => {
          const id = param.data?.statusId ?? null;
          const statuses = this.genericStore.investmentStatusEntities();
          const statusEnt = statuses.find((s) => s.id === id);
          const name = statusEnt?.name?.trim();
          const view: BadgeView = toInvestmentBadgeView(name);
          const descKey = `admin.investments.statusDescriptions.${name ?? ''}`;
          const tooltipText = this.translate.instant(descKey);
          if (tooltipText && tooltipText !== descKey) {
            view.infoTooltipKey = tooltipText;
          }
          return { view };
        },
        minWidth: 200,
        flex: 0,
      },
      {
        colId: 'actions',
        headerName: '',
        cellRenderer: InvestmentActionCellRendererComponent,
        cellRendererParams: (p: ICellRendererParams<IncomingRow>) => ({
          ...p,
          onAccept: (row: IncomingRow) => this.handleAccept(row),
          onReject: (row: IncomingRow) => this.handleRefuse(row),
          onApprove: (row: IncomingRow) => this.handleApprove(row),
        }),
        width: 140,
        flex: 0,
        cellClass: 'd-flex justify-content-center align-items-center',
        sortable: false,
        filter: false,
      },
    ];
  }

  private loadRows(params: IGetRowsParams): void {
    this.loading = true;

    const skip: number = params.startRow;
    const take: number = params.endRow - params.startRow;

    const sortModel = params.sortModel ?? [];
    const sort: ComplexPaginatedDataRequest['sort'] =
      sortModel.length > 0
        ? sortModel.map((s) => ({
            selector: s.colId,
            desc: s.sort === 'desc',
          }))
        : [];

    this.service
      .getInvestmentsByProjectId(this.projectId, { skip, take, sort })
      .pipe(
        switchMap((page: PaginatedData<InvestmentResponse>) => {
          const list = (page?.data ?? []) as InvestmentResponse[];
          const rowCount: number =
            page?.rowCount != null ? page.rowCount : list.length;

          const userIds = Array.from(
            new Set(
              list
                .map((x) => x.userId)
                .filter((x): x is string => typeof x === 'string' && !!x),
            ),
          );
          const hasLegalEntities = list.some(
            (x) => typeof x.legalEntityId === 'string' && !!x.legalEntityId,
          );

          const users$ =
            userIds.length > 0
              ? forkJoin(userIds.map((id) => this.userService.getUserById(id)))
              : of([] as UserAccount[]);
          const legalEntities$ = hasLegalEntities
            ? this.legalEntityService.getLegalEntitiesAdmin({
                skip: 0,
                take: 10000,
              }).pipe(
                map((res) => {
                  const data = res?.data ?? [];
                  const map = new Map<string, string | null>();
                  for (const entity of data) {
                    if (entity?.id) {
                      map.set(entity.id, entity.name ?? null);
                    }
                  }
                  return map;
                }),
                catchError(() => of(new Map<string, string | null>())),
              )
            : of(new Map<string, string | null>());

          return forkJoin({ users: users$, legalEntities: legalEntities$ }).pipe(
            map(({ users, legalEntities: legalEntityNames }) => {
              const byId = new Map<string, UserAccount>();
              for (const u of users as UserAccount[]) {
                if (u?.id) {
                  byId.set(u.id, u);
                }
              }

              const rows: IncomingRow[] = list.map((inv) => {
                const sentRaw = inv.sentDate ?? '';
                const pendingRaw = inv.pendingUntil ?? null;
                const user = inv.userId ? byId.get(inv.userId) : undefined;

                const userName = user?.personalData
                  ? `${user.personalData.firstName} ${user.personalData.lastName}`.trim()
                  : (user?.userName ?? inv.userId ?? '—');
                const legalEntityName =
                  inv.legalEntityId &&
                  legalEntityNames.get(inv.legalEntityId)?.trim();
                const bidderName = legalEntityName
                  ? `${legalEntityName} (${userName})`
                  : userName;

                return {
                  id: inv.id,
                  sentRaw,
                  sentDate: formatDate(sentRaw),
                  sentTime: formatTime(sentRaw),

                  identifier: inv.identifier ?? '—',

                  bidderName,
                  bidderEmail: user?.email ?? null,
                  bidderType: inv.legalEntityId,
                  investorIsExperienced: inv.investorIsExperienced ?? false,

                  amount: inv.amount ?? null,
                  currency: inv.projectCurrency ?? null,

                  interestRate: null,

                  pendingUntilRaw: pendingRaw,
                  pendingUntilDate: pendingRaw ? formatDate(pendingRaw) : '—',
                  pendingUntilTime: pendingRaw ? formatTime(pendingRaw) : '',
                  pendingUntilCountdown: pendingRaw
                    ? formatCountdownTo(pendingRaw)
                    : '',
                  acceptUntilTarget:
                    inv.status === InvestmentStatus.UnderReview && sentRaw
                      ? addHours(sentRaw, 24)?.toISOString() ?? null
                      : null,
                  investmentProtection: inv.investmentProtection ?? false,

                  statusId: inv.status ?? null,
                  actionType: getActionType(inv),
                };
              });

              return { rows, rowCount };
            }),
          );
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: ({ rows, rowCount }) => {
          params.successCallback(rows, rowCount);
        },
        error: () => {
          params.failCallback();
          this.toast.error(
            this.translate.instant('admin.common.toasts.dataLoadFailed'),
          );
        },
      });
  }

  private handleAccept(row: IncomingRow): void {
    this.service.acceptInvestment(row.id).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant(
            'admin.allProjects.toasts.investmentConfirmed',
          ),
        );
        this.reloadFirstPage();
      },
      error: () => {
        this.toast.error(
          this.translate.instant(
            'admin.allProjects.toasts.investmentConfirmFailed',
          ),
        );
      },
    });
  }

  private handleRefuse(row: IncomingRow): void {
    this.service.refuseInvestment(row.id).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant('admin.allProjects.toasts.investmentRejected'),
        );
        this.reloadFirstPage();
      },
      error: () => {
        this.toast.error(
          this.translate.instant(
            'admin.allProjects.toasts.investmentRejectFailed',
          ),
        );
      },
    });
  }

  private handleApprove(row: IncomingRow): void {
    const investmentId = row.id;

    this.service.markInvestmentAsCredited(investmentId).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant('admin.allProjects.toasts.investmentApproved'),
        );
        this.reloadFirstPage();
      },
      error: () => {
        this.toast.error(
          this.translate.instant(
            'admin.allProjects.toasts.investmentApproveFailed',
          ),
        );
      },
    });
  }

  private reloadFirstPage(): void {
    this.datasource = {
      getRows: (params: IGetRowsParams): void => this.loadRows(params),
    };
  }
}
